import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  Briefcase, CalendarCheck, ChevronRight, ClipboardList, Compass, Download, Home,
  LogOut, Plus, RefreshCcw, ShoppingCart, Sparkles, Trophy, Upload, WalletCards
} from 'lucide-react';
import { DataContext, AppContext } from './context.jsx';
import { reducer } from './reducer.js';
import { normalizeData, applyDailyReset, starterData } from './lib/normalizers.js';
import { getTodayKey, getCurrentWeekDay, isChoreDueToday } from './lib/helpers.js';
import { loadFromDb, saveToDb, clearDb, loadUserData, saveUserData } from './lib/db.js';
import { supabase } from './lib/supabase.js';
import { NavButton } from './components/ui.jsx';
import Auth from './views/Auth.jsx';
import Crm from './views/Crm.jsx';
import Dashboard from './views/Dashboard.jsx';
import Planner from './views/Planner.jsx';
import Chores from './views/Chores.jsx';
import Budget from './views/Budget.jsx';
import Shopping from './views/Shopping.jsx';
import Rewards from './views/Rewards.jsx';

const VIEW_TITLES = {
  dashboard: 'Command dashboard',
  planner: 'Daily planner',
  chores: 'Chore tracking',
  budget: 'Budget center',
  shopping: 'Shopping hub',
  rewards: 'Streaks and rewards',
  crm: 'CRM'
};

function timeoutAfter(ms, label) {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(`${label} timed out`)), ms);
  });
}

export default function App() {
  const [data, dispatch] = useReducer(reducer, null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [activeView, setActiveView] = useState('dashboard');
  const [drafts, setDrafts] = useState({
    chore: '', pantry: '', supply: '', grocery: '',
    store: '', receiptStore: '', receiptText: '', goal: '', reward: ''
  });
  const saveTimerRef = useRef(null);
  const latestDataRef = useRef(null);
  const skipNextPersistRef = useRef(false);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [crmOpen, setCrmOpen] = useState(false);
  const [activeCrmProject, setActiveCrmProject] = useState(null);

  // Auth state listener + initial data load
  useEffect(() => {
    let authSubscription = null;

    const fallback = async () => {
      const saved = await loadFromDb().catch(() => null);
      dispatch({ type: 'RESET', data: applyDailyReset(normalizeData(saved || starterData)) });
      setLoading(false);
    };

    const init = async () => {
      // Race getSession() against a 4s timeout — if CSP or network blocks it, fall through
      let session = null;
      try {
        const result = await Promise.race([
          supabase.auth.getSession(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('auth-timeout')), 4000))
        ]);
        session = result.data.session;
        setSession(session);
      } catch (err) {
        console.warn('Auth init skipped:', err.message);
        await fallback();
        return;
      }
      try {
        if (session) {
          const saved = await loadUserData(session.user.id);
          dispatch({ type: 'RESET', data: applyDailyReset(normalizeData(saved || starterData)) });
        } else {
          const saved = await loadFromDb().catch(() => null);
          dispatch({ type: 'RESET', data: applyDailyReset(normalizeData(saved || starterData)) });
        }
      } catch {
        dispatch({ type: 'RESET', data: applyDailyReset(normalizeData(starterData)) });
      } finally {
        setLoading(false);
      }
    };

    init();

    // Handle subsequent sign-in / sign-out
    try {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
        if (event === 'SIGNED_IN') {
          setSession(newSession);
          const saved = await loadUserData(newSession.user.id);
          dispatch({ type: 'RESET', data: applyDailyReset(normalizeData(saved || starterData)) });
          setLoading(false);
        } else if (event === 'SIGNED_OUT') {
          setSession(null);
          skipNextPersistRef.current = true;
          dispatch({ type: 'RESET', data: applyDailyReset(normalizeData(starterData)) });
          setLoading(false);
        }
      });
      authSubscription = subscription;
    } catch (err) {
      console.error('Auth listener error:', err);
    }

    return () => authSubscription?.unsubscribe();
  }, []);

  // Persist to IDB immediately; debounce remote save to Supabase
  useEffect(() => {
    if (loading || !data) return;
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    latestDataRef.current = data;
    saveToDb(data);
    if (session) {
      clearTimeout(saveTimerRef.current);
      setSyncStatus('saving');
      saveTimerRef.current = setTimeout(async () => {
        try {
          await saveUserData(session.user.id, data);
          setSyncStatus('saved');
        } catch {
          setSyncStatus('error');
        }
      }, 1500);
    }
    return () => clearTimeout(saveTimerRef.current);
  }, [data, loading, session]);

  const updateData = (recipe) => dispatch({ type: 'APPLY', recipe });

  const stats = useMemo(() => {
    if (!data) return { expenses: 0, income: 0, completed: 0, dueTodayCount: 0, lowStock: 0, topStreak: 0 };
    const expenses = data.transactions
      .filter((item) => item.type === 'expense')
      .reduce((sum, item) => sum + Number(item.amount), 0);
    const income = data.transactions
      .filter((item) => item.type === 'income')
      .reduce((sum, item) => sum + Number(item.amount), 0);
    const dueToday = data.chores.filter((item) => isChoreDueToday(item));
    const completed = dueToday.filter((item) => item.doneToday).length;
    const lowStock = [...data.pantry, ...data.supplies].filter((item) => item.low).length;
    const topStreak = Math.max(...data.chores.map((item) => item.streak), 0);
    return { expenses, income, completed, dueTodayCount: dueToday.length, lowStock, topStreak };
  }, [data]);

  const addItem = (collection, draftKey, createItem) => {
    const value = drafts[draftKey].trim();
    if (!value) return;
    updateData((current) => ({ ...current, [collection]: [...current[collection], createItem(value)] }));
    setDrafts((current) => ({ ...current, [draftKey]: '' }));
  };

  const removeItem = (collection, id) => {
    updateData((current) => ({ ...current, [collection]: current[collection].filter((item) => item.id !== id) }));
  };

  const toggleChore = (id) => {
    const today = getTodayKey();
    let pointsDelta = 0;
    updateData((current) => ({
      ...current,
      chores: current.chores.map((item) => {
        if (item.id !== id) return item;
        const doneToday = !item.doneToday;
        pointsDelta = doneToday ? 10 : -10;
        return { ...item, doneToday, lastCompletedDate: doneToday ? today : null, streak: Math.max(0, item.streak + (doneToday ? 1 : -1)) };
      }),
      points: Math.max(0, current.points + pointsDelta)
    }));
  };

  const moveChoreToDay = (id, day) => {
    updateData((current) => ({
      ...current,
      chores: current.chores.map((item) => (
        item.id === id
          ? {
              ...item,
              cadence: day === 'Daily' ? 'Daily' : item.cadence === 'Daily' ? 'Weekly' : item.cadence,
              weekDay: day === 'Daily' ? undefined : day
            }
          : item
      ))
    }));
  };

  const setChoreCadence = (id, cadence) => {
    updateData((current) => ({
      ...current,
      chores: current.chores.map((item) => (
        item.id === id
          ? { ...item, cadence, weekDay: item.weekDay || getCurrentWeekDay() }
          : item
      ))
    }));
  };

  const exportData = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lifeos-${getTodayKey()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = (file, input) => {
    if (!file) return;
    if (!window.confirm('Replace all data with this backup? Current data will be overwritten.')) {
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.chores)) {
          window.alert('Invalid backup file — expected a lifeOS export.');
          return;
        }
        updateData(() => applyDailyReset(normalizeData(parsed)));
      } catch {
        window.alert('Could not read backup file. Make sure it is a valid lifeOS export.');
      } finally {
        input.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleAddProject = () => {
    const name = window.prompt('Project name:');
    if (!name?.trim()) return;
    const id = crypto.randomUUID();
    updateData((current) => ({
      ...current,
      crm: { projects: [...(current.crm?.projects || []), {
        id, name: name.trim(),
        statuses: ['Not Started', 'Deferred', 'In Progress', 'Completed', 'Waiting for input'],
        tasks: []
      }] }
    }));
    setActiveCrmProject(id);
    setActiveView('crm');
    setCrmOpen(true);
  };

  const resetDemo = () => {
    if (!window.confirm('Reset all data to defaults? This cannot be undone.')) return;
    clearDb();
    dispatch({ type: 'RESET', data: applyDailyReset(normalizeData(starterData)) });
  };

  const signOut = async () => {
    clearTimeout(saveTimerRef.current);
    setSyncStatus('saving');

    try {
      if (session && latestDataRef.current) {
        await Promise.race([
          saveUserData(session.user.id, latestDataRef.current),
          timeoutAfter(4000, 'Final cloud save')
        ]);
        setSyncStatus('saved');
      }
    } catch (err) {
      console.error('Final cloud save before sign-out failed:', err);
      setSyncStatus('error');
    }

    try {
      await Promise.race([
        supabase.auth.signOut(),
        timeoutAfter(4000, 'Sign out')
      ]);
    } catch (err) {
      console.error('Supabase sign-out failed, forcing local sign-out:', err);
      skipNextPersistRef.current = true;
      setSession(null);
      dispatch({ type: 'RESET', data: applyDailyReset(normalizeData(starterData)) });
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="brand-mark"><Home size={24} /></div>
        <p>Loading lifeOS…</p>
      </div>
    );
  }

  if (!session) {
    return <Auth />;
  }

  const dataCtx = { data, updateData };
  const appCtx = {
    activeView, setActiveView, stats, drafts, setDrafts,
    addItem, removeItem, toggleChore, moveChoreToDay, setChoreCadence,
    exportData, importData, resetDemo, activeCrmProject
  };

  return (
    <DataContext.Provider value={dataCtx}>
      <AppContext.Provider value={appCtx}>
        <div className="app-shell">
          <aside className="sidebar">
            <div className="brand-lockup">
              <div className="brand-mark"><Home size={20} /></div>
              <div>
                <p className="eyebrow">Personal command center</p>
                <h1>lifeOS</h1>
              </div>
            </div>
            <nav className="nav-list" aria-label="lifeOS views">
              <NavButton icon={ClipboardList} label="Dashboard" value="dashboard" activeView={activeView} setActiveView={setActiveView} />
              <NavButton icon={Compass} label="Planner" value="planner" activeView={activeView} setActiveView={setActiveView} />
              <NavButton icon={CalendarCheck} label="Chores" value="chores" activeView={activeView} setActiveView={setActiveView} />
              <NavButton icon={WalletCards} label="Budget" value="budget" activeView={activeView} setActiveView={setActiveView} />
              <NavButton icon={ShoppingCart} label="Shopping" value="shopping" activeView={activeView} setActiveView={setActiveView} />
              <NavButton icon={Trophy} label="Rewards" value="rewards" activeView={activeView} setActiveView={setActiveView} />
              <div className="nav-crm-section">
                <button
                  className={`nav-button crm-toggle ${activeView === 'crm' || crmOpen ? 'crm-section-open' : ''}`}
                  onClick={() => setCrmOpen((o) => !o)}
                  type="button"
                >
                  <Briefcase size={18} />
                  <span>CRM</span>
                  <ChevronRight size={13} className={`crm-chevron${crmOpen ? ' open' : ''}`} />
                </button>
                {crmOpen && (
                  <div className="crm-project-list">
                    {data.crm?.projects?.map((p) => (
                      <button
                        key={p.id}
                        className={`crm-project-item${activeCrmProject === p.id && activeView === 'crm' ? ' active' : ''}`}
                        onClick={() => { setActiveCrmProject(p.id); setActiveView('crm'); }}
                        type="button"
                      >
                        {p.name}
                      </button>
                    ))}
                    <button className="crm-add-project" onClick={handleAddProject} type="button">
                      <Plus size={13} /> New project
                    </button>
                  </div>
                )}
              </div>
            </nav>
            <div className="sidebar-actions">
              <button className="ghost-button" onClick={exportData} type="button">
                <Download size={16} /> Export backup
              </button>
              <label className="ghost-button">
                <Upload size={16} /> Import backup
                <input accept=".json" type="file" onChange={(e) => importData(e.target.files?.[0], e.target)} />
              </label>
              <button className="ghost-button reset-button" onClick={resetDemo} type="button">
                <RefreshCcw size={16} /> Reset to defaults
              </button>
              <div className="sidebar-user">
                <span className="user-email">{session.user.email}</span>
                {session && (
                  <span className={syncStatus === 'error' ? 'sync-status error' : 'sync-status'}>
                    {syncStatus === 'saving' ? 'Saving to cloud...' : syncStatus === 'saved' ? 'Cloud saved' : syncStatus === 'error' ? 'Cloud save failed' : 'Cloud sync ready'}
                  </span>
                )}
                <button className="ghost-button" onClick={signOut} type="button">
                  <LogOut size={16} /> Sign out
                </button>
              </div>
            </div>
          </aside>

          <main>
            <header className="topbar">
              <div>
                <p className="eyebrow">Today</p>
                <h2>{activeView === 'crm'
                  ? (data.crm?.projects?.find((p) => p.id === activeCrmProject)?.name || 'CRM')
                  : VIEW_TITLES[activeView]}</h2>
              </div>
              <div className="points-pill">
                <Sparkles size={18} />
                <span>{data.points} pts</span>
              </div>
            </header>

            {activeView === 'dashboard' && <Dashboard />}
            {activeView === 'planner' && <Planner />}
            {activeView === 'chores' && <Chores />}
            {activeView === 'budget' && <Budget />}
            {activeView === 'shopping' && <Shopping />}
            {activeView === 'rewards' && <Rewards />}
            {activeView === 'crm' && <Crm />}
          </main>
        </div>
      </AppContext.Provider>
    </DataContext.Provider>
  );
}
